/** EMBER & PALM — practical location page with a real map, concise transport guidance and current hours. */
import { Bus, MapPin, Navigation, Phone, TramFront } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { SITE, trackEvent } from "@/lib/site";

export default function LocationPage() {
  return (
    <>
      <PageMeta
        title="Contact & Opening Hours | ANGKOR Cambodian Flavours"
        description="389 Spencer Street, West Melbourne VIC 3003. Phone (03) 7034 8353. Opening hours, directions and parking near Southern Cross and Marvel Stadium."
        path="/location"
        image={SITE.images.fishAmok}
      />
      <section className="location-hero">
        <div className="container location-hero-grid">
          <div>
            <p className="eyebrow">Find ANGKOR</p>
            <h1>
              Cambodian dining in the heart of <em>West Melbourne.</em>
            </h1>
            <p>
              {SITE.address}. Convenient to Southern Cross Station, Melbourne
              CBD, Docklands and Marvel Stadium.
            </p>
            <div className="inline-actions">
              <a
                className="button button-gold"
                href={SITE.directions}
                target="_blank"
                rel="noreferrer"
                aria-label="Get directions (opens in a new tab)"
                onClick={() =>
                  trackEvent("directions_click", { placement: "location_hero" })
                }
              >
                <Navigation size={17} /> Get Directions
              </a>
              <a
                className="text-link light"
                href={`tel:${SITE.phone}`}
                onClick={() =>
                  trackEvent("phone_click", { placement: "location_hero" })
                }
              >
                <Phone size={16} /> {SITE.phoneDisplay}
              </a>
            </div>
          </div>
          <figure className="location-hero-media">
            <img
              src={SITE.images.interior800}
              srcSet={`${SITE.images.interior480} 480w, ${SITE.images.interior800} 800w, ${SITE.images.interior1200} 1200w`}
              sizes="(max-width: 767px) calc(100vw - 32px), 48vw"
              alt="Dining room at ANGKOR Cambodian Flavours in West Melbourne"
              width="1800"
              height="1200"
            />
            <figcaption>
              <img
                src={SITE.images.fishAmok640}
                srcSet={`${SITE.images.fishAmok480} 480w, ${SITE.images.fishAmok640} 640w, ${SITE.images.fishAmok960} 960w`}
                sizes="(max-width: 767px) 40vw, 280px"
                alt="Fish Amok served in banana leaf at ANGKOR"
                width="900"
                height="1125"
              />
              <span>
                Arrive for West Melbourne.
                <br />
                <strong>Stay for Fish Amok.</strong>
              </span>
            </figcaption>
          </figure>
        </div>
      </section>
      <section
        className="map-section"
        aria-label="Illustrated proximity map of ANGKOR Cambodian Flavours and nearby landmarks"
      >
        <svg
          className="angkor-map"
          viewBox="0 0 1400 520"
          role="img"
          aria-labelledby="map-title map-description"
        >
          <title id="map-title">
            ANGKOR Cambodian Flavours in West Melbourne
          </title>
          <desc id="map-description">
            Illustrated proximity map showing ANGKOR on Spencer Street near
            Southern Cross Station, Marvel Stadium, Flagstaff Gardens and
            Melbourne CBD.
          </desc>
          <rect width="1400" height="520" fill="#151210" />
          <path
            d="M0 190 C260 145 450 180 690 150 S1110 100 1400 132"
            fill="none"
            stroke="#3c3731"
            strokeWidth="44"
          />
          <path
            d="M0 190 C260 145 450 180 690 150 S1110 100 1400 132"
            fill="none"
            stroke="#9f7b35"
            strokeOpacity=".32"
            strokeWidth="2"
            strokeDasharray="16 12"
          />
          <path
            d="M530 -30 L565 550"
            fill="none"
            stroke="#49423b"
            strokeWidth="58"
          />
          <path
            d="M530 -30 L565 550"
            fill="none"
            stroke="#d0a24a"
            strokeOpacity=".55"
            strokeWidth="3"
          />
          <path
            d="M860 -40 L900 545"
            fill="none"
            stroke="#312d29"
            strokeWidth="36"
          />
          <path
            d="M195 0 L248 520"
            fill="none"
            stroke="#2e2a26"
            strokeWidth="30"
          />
          <rect
            x="70"
            y="265"
            width="245"
            height="150"
            rx="2"
            fill="#203221"
            stroke="#62845f"
            strokeWidth="2"
          />
          <rect
            x="955"
            y="230"
            width="250"
            height="175"
            rx="90"
            fill="#20252a"
            stroke="#777b7d"
            strokeWidth="3"
          />
          <rect
            x="635"
            y="245"
            width="125"
            height="120"
            fill="#26211e"
            stroke="#665b50"
          />
          <circle cx="548" cy="237" r="19" fill="#d0a24a" />
          <path
            d="M548 218 C527 218 516 235 522 252 C528 270 548 286 548 286 C548 286 568 270 574 252 C580 235 569 218 548 218Z"
            fill="#d0a24a"
          />
          <circle cx="548" cy="244" r="7" fill="#151210" />
          <g fill="#f5eddd" fontFamily="Manrope, sans-serif">
            <text x="585" y="238" fontSize="22" fontWeight="800">
              ANGKOR
            </text>
            <text x="585" y="264" fontSize="14" fill="#c7bda9">
              389 Spencer Street
            </text>
            <text x="92" y="300" fontSize="18" fontWeight="700">
              Flagstaff Gardens
            </text>
            <text x="975" y="320" fontSize="20" fontWeight="700">
              Marvel Stadium
            </text>
            <text x="642" y="390" fontSize="18" fontWeight="700">
              Southern Cross
            </text>
            <text x="642" y="412" fontSize="14" fill="#c7bda9">
              Station
            </text>
            <text x="1030" y="90" fontSize="18" fontWeight="700">
              Melbourne CBD
            </text>
            <text
              x="505"
              y="475"
              fontSize="14"
              fill="#d0a24a"
              letterSpacing="2"
            >
              SPENCER STREET
            </text>
          </g>
        </svg>
        <div className="map-address">
          <MapPin />
          <div>
            <strong>{SITE.name}</strong>
            <span>{SITE.address}</span>
            <a
              href={SITE.directions}
              target="_blank"
              rel="noreferrer"
              aria-label="Open live directions (opens in a new tab)"
              onClick={() =>
                trackEvent("directions_click", { placement: "map" })
              }
            >
              Open live directions
            </a>
          </div>
        </div>
        <p className="map-note">
          Illustrated proximity map · Use live directions for your exact route.
        </p>
      </section>
      <section className="section parchment-section">
        <div className="container location-details-grid">
          <div>
            <p className="eyebrow dark">Visit</p>
            <h2>Opening hours</h2>
            <div className="hours-list">
              {SITE.hours.map(([day, hours]) => (
                <div key={day}>
                  <span>{day}</span>
                  <strong>{hours}</strong>
                </div>
              ))}
            </div>
            <small>
              Standard restaurant dining: 10% surcharge Sundays · 15% public
              holidays · Published prices include GST. Event package surcharges
              are listed separately on the Events page.
            </small>
          </div>
          <div>
            <p className="eyebrow dark">Plan your journey</p>
            <h2>Getting here</h2>
            <div className="transport-list">
              <article>
                <TramFront />
                <div>
                  <h3>Southern Cross Station</h3>
                  <p>
                    A short walk along Spencer Street. Use current maps for the
                    route that suits your platform exit.
                  </p>
                </div>
              </article>
              <article>
                <Bus />
                <div>
                  <h3>Tram & bus</h3>
                  <p>
                    Spencer Street is served by central Melbourne public
                    transport. Check PTV for current services and disruptions.
                  </p>
                </div>
              </article>
              <article>
                <Navigation />
                <div>
                  <h3>Driving & parking</h3>
                  <p>
                    Street and commercial parking conditions change. Check
                    current signs and nearby operators before travel.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
      <section className="final-cta">
        <div className="container">
          <p className="eyebrow dark">Arriving tonight?</p>
          <h2>
            Book ahead, <em>then come hungry.</em>
          </h2>
          <a
            className="button button-gold"
            href={SITE.booking}
            onClick={() =>
              trackEvent("book_table_click", { placement: "location_bottom" })
            }
          >
            Book a Table
          </a>
        </div>
      </section>
    </>
  );
}
